import { create } from 'zustand';
import { supabase } from './supabase';
import { logUserSession } from './sessionLogs';
import { clearTenantDataStores } from './utils/tenantCache';
import { initializeHospitalContext, syncHospitalIdFromMetadata } from './hospitalContext';
import { AuthUser } from '@/types';

export interface AuthState {
  user: any | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: { email: string; password: string }) => Promise<boolean>;
  logout: () => Promise<void>;
  initialize: () => Promise<(() => void) | undefined>;
  checkCurrentUserStatus: () => Promise<void>;
}

// Utility function to check if current user is still active
const checkUserStatus = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { data: profile, error } = await supabase
      .from('profiles')
      .select('status')
      .eq('id', user.id)
      .single();

    if (error || !profile) return false;
    // Allow 'verified' status (email verification) - treat as active
    // Also allow 'active' for backward compatibility
    return profile.status === 'verified' || profile.status === 'active';
  } catch (error) {
    console.error('Error checking user status:', error);
    return false;
  }
};

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: false,

  login: async (credentials) => {
    set({ isLoading: true });
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password,
      });

      if (error) throw error;

      clearTenantDataStores();
      await syncHospitalIdFromMetadata();

      // Get user profile with role - only select needed fields for faster query
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('role, name, full_name, status, hospital_id')
        .eq('id', data.user.id)
        .single();

      if (profileError) {
        console.error('Profile error:', profileError);
        throw profileError;
      }

      // Check if user email is verified (or active for backward compatibility)
      // Only allow login if status is 'verified' or 'active'
      if (profile.status !== 'verified' && profile.status !== 'active') {
        console.error('User account is not verified:', profile.status);
        throw new Error('Your email is not verified. Please verify your email or contact an administrator.');
      }

      // Ensure email is always a string for AuthUser interface
      const userEmail = data.user.email || '';
      const userName = profile.name || profile.full_name || userEmail.split('@')[0] || 'Unknown User';
      
      const userWithRole: AuthUser = { 
        id: data.user.id,
        email: userEmail,
        role: profile.role,
        name: userName,
        hospitalId: profile.hospital_id ?? undefined,
      };

      set({ 
        user: userWithRole, 
        isAuthenticated: true,
        isLoading: false
      });

      // Log the login event asynchronously (don't block login)
      logUserSession({
        user: userWithRole, // Now properly typed as AuthUser
        action: 'login',
        details: 'User logged in successfully',
        ipAddress: '', // Could be enhanced to get real IP
        deviceInfo: navigator.userAgent
      }).catch(logError => {
        console.error('Error logging login event:', logError);
      });

      // Initialize hospital context
      initializeHospitalContext().catch(err => {
        console.error('Error initializing hospital context:', err);
      });

      return true;
    } catch (error) {
      console.error('Login error:', error);
      set({ user: null, isAuthenticated: false, isLoading: false });
      return false;
    }
  },

  logout: async () => {
    try {
      console.log('Starting logout process...');
      
      // Get current user before clearing state
      const currentUser = useAuthStore.getState().user;
      
      // Log the logout event FIRST to ensure RLS-allowed insert while session is valid
      if (currentUser) {
        try {
          await logUserSession({
            user: currentUser,
            action: 'logout',
            details: 'User logged out successfully'
          });
        } catch (logError) {
          console.error('Error logging logout event (pre-signout):', logError);
        }
      }
      
      // Clear auth state FIRST to prevent auto-login
      set({ user: null, isAuthenticated: false });
      clearTenantDataStores();
      
      // Clear all local storage and session storage
      try {
        localStorage.clear();
        sessionStorage.clear();
        
        // Clear specific Supabase auth tokens
        localStorage.removeItem('amcare-auth-token');
        localStorage.removeItem('supabase.auth.token');
        localStorage.removeItem('supabase.auth.expires_at');
        localStorage.removeItem('supabase.auth.refresh_token');
        localStorage.removeItem('supabase.auth.access_token');
        
        // Clear any other potential auth keys
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (key.includes('auth') || key.includes('supabase') || key.includes('token'))) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));
      } catch (storageError) {
        console.warn('Error clearing storage:', storageError);
      }
      
      // Sign out from Supabase
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.warn('Supabase signOut error:', error);
        // Continue with logout even if Supabase signOut fails
      }
      
      // Force clear any remaining session data
      try {
        // Clear session by setting empty session
        await supabase.auth.setSession({
          access_token: '',
          refresh_token: ''
        });
      } catch (sessionError) {
        console.warn('Error clearing session:', sessionError);
      }
      
      console.log('Logout completed successfully');
    } catch (error) {
      console.error('Logout error:', error);
      // Even if there's an error, ensure local state is cleared
      set({ user: null, isAuthenticated: false });
      
      // Force clear storage on error
      try {
        localStorage.clear();
        sessionStorage.clear();
      } catch (storageError) {
        console.warn('Error clearing storage on logout error:', storageError);
      }
    }
  },

  initialize: async () => {
    set({ isLoading: true });
    try {
      // Check if we're coming from a logout
      const urlParams = new URLSearchParams(window.location.search);
      const isLogout = urlParams.get('logout');
      const isError = urlParams.get('error');
      
      // If this is a logout, don't auto-authenticate
      if (isLogout || isError) {
        set({ user: null, isAuthenticated: false, isLoading: false });
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }
      
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        await syncHospitalIdFromMetadata();

        // Get user profile with role - only select needed fields for faster query
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('role, name, full_name, status, hospital_id')
          .eq('id', session.user.id)
          .single();

        if (profileError) throw profileError;

        // Check if user email is verified (or active for backward compatibility)
        if (profile.status !== 'verified' && profile.status !== 'active') {
          console.error('User account is not verified:', profile.status);
          // Logout the user if their email is not verified
          await supabase.auth.signOut();
          set({ user: null, isAuthenticated: false, isLoading: false });
          return;
        }

        // Create properly typed AuthUser object
        const userEmail = session.user.email || '';
        const userName = profile.name || profile.full_name || userEmail.split('@')[0] || 'Unknown User';
        const authenticatedUser: AuthUser = {
          id: session.user.id,
          email: userEmail,
          role: profile.role,
          name: userName,
          hospitalId: profile.hospital_id ?? undefined,
        };

        set({ 
          user: authenticatedUser, 
          isAuthenticated: true,
          isLoading: false
        });
      } else {
        set({ user: null, isAuthenticated: false, isLoading: false });
      }

      // Set up auth state listener
      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        async (event, session) => {
          console.log('Auth state changed:', event, session?.user?.id);
          
          if (event === 'SIGNED_IN' && session) {
            // Only fetch profile if we don't already have user data
            const currentState = useAuthStore.getState();
            if (!currentState.user || currentState.user.id !== session.user.id) {
              try {
                // Get user profile with role - only select needed fields for faster query
                const { data: profile, error: profileError } = await supabase
                  .from('profiles')
                  .select('role, name, full_name, status, hospital_id')
                  .eq('id', session.user.id)
                  .single();

                if (profileError) throw profileError;

                // Check if user email is verified (or active for backward compatibility)
                if (profile.status !== 'verified' && profile.status !== 'active') {
                  console.error('User account is not verified on auth change:', profile.status);
                  await supabase.auth.signOut();
                  set({ user: null, isAuthenticated: false });
                  return;
                }

                // Create properly typed AuthUser object
                const userEmail = session.user.email || '';
                const userName = profile.name || profile.full_name || userEmail.split('@')[0] || 'Unknown User';
                const authenticatedUser: AuthUser = {
                  id: session.user.id,
                  email: userEmail,
                  role: profile.role,
                  name: userName,
                  hospitalId: profile.hospital_id ?? undefined,
                };

                set({ 
                  user: authenticatedUser, 
                  isAuthenticated: true 
                });
              } catch (error) {
                console.error('Error getting profile on auth state change:', error);
                set({ user: null, isAuthenticated: false });
              }
            }
          } else if (event === 'SIGNED_OUT') {
            set({ user: null, isAuthenticated: false });
            clearTenantDataStores();
          }
          // Don't handle TOKEN_REFRESHED as it doesn't change user state
        }
      );

      // Set up periodic status check for inactive users
      const statusCheckInterval = setInterval(async () => {
        const isActive = await checkUserStatus();
        if (!isActive) {
          console.log('User status check failed - logging out');
          await supabase.auth.signOut();
          set({ user: null, isAuthenticated: false });
        }
      }, 30000); // Check every 30 seconds

      // Return cleanup function
      return () => {
        subscription.unsubscribe();
        clearInterval(statusCheckInterval);
      };
    } catch (error) {
      console.error('Auth initialization error:', error);
      set({ user: null, isAuthenticated: false, isLoading: false });
    }
  },

  checkCurrentUserStatus: async () => {
    try {
      const isActive = await checkUserStatus();
      if (!isActive) {
        console.log('User status check failed - logging out');
        await supabase.auth.signOut();
        set({ user: null, isAuthenticated: false });
      }
    } catch (error) {
      console.error('Error checking current user status:', error);
      // On error, log out for safety
      await supabase.auth.signOut();
      set({ user: null, isAuthenticated: false });
    }
  },
}));
